import React, { useMemo } from "react";
import styled from "styled-components";
import { isEmpty, uniqBy } from "lodash";
import { EditorRenderProps } from "@sonder/editor";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { UserCard } from "@sonder/ui/source/styled/components/UserCard";
import { colors } from "@sonder/ui/source/styled/theme";
import { useDebounce } from "core/hooks/useDebounce";
import { UserData, useSearchForUsers } from "core/queries/SearchForUsers";

const NoMatches = styled.span`
  flex-grow: 1;
  align-self: center;
  justify-self: center;
  text-align: center;
`;

const StyledList = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  min-height: 3.2rem;
  max-height: 20rem;
  min-width: 15rem;
  max-width: 19rem;
  overflow-y: auto;
  > button {
    border-color: transparent;
    margin: 0.1rem 0;
  }
`;

const StyledUserCard = styled(UserCard)`
  flex-shrink: 0;
  flex-grow: 1;
  justify-content: flex-start;
  button + button {
    margin-top: 0.5rem;
  }

  > svg {
    color: ${colors.gray6};
    flex-shrink: 0;
    width: 1rem;
  }
`;

const LoadingList = () => (
  <StyledList>
    <NoMatches>Searching...</NoMatches>
  </StyledList>
);

const NoMatchesList = () => (
  <StyledList>
    <NoMatches>No users found</NoMatches>
  </StyledList>
);

const stripAtMention = (str: string) =>
  str.startsWith("@") ? str.slice(1) : str;

export const UserMentionSearch = ({
  searchTerm,
  searchTermPosition,
  applyUserMention,
}: EditorRenderProps) => {
  if (!searchTerm || !searchTermPosition || searchTerm === "@") return null;

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, error, loading } = useSearchForUsers({
    variables: { searchTerm: stripAtMention(debouncedSearchTerm) },
  });

  const users = useMemo(() => {
    return data?.searchWithMetadata?.items.map(item => item.resource) ?? [];
  }, [data, error, loading]);

  return (
    <Dropdown isOpen boundingRect={searchTermPosition}>
      {() => (
        <DropdownContent>
          {loading && isEmpty(users) && <LoadingList />}
          {!loading && isEmpty(users) && <NoMatchesList />}
          {!isEmpty(users) && (
            <StyledList>
              {uniqBy(users, "id")
                .filter(u => u.__typename === "User")
                .map(u => {
                  const { email, id: userId, name, avatar } = u as UserData;
                  if (!name) return null;
                  return (
                    <StyledUserCard
                      fullWidth
                      noBorder
                      email={email}
                      imageUrl={avatar?.url}
                      key={userId}
                      name={name}
                      onClick={() =>
                        applyUserMention({ name: `@${name}`, userId })
                      }
                    />
                  );
                })}
            </StyledList>
          )}
        </DropdownContent>
      )}
    </Dropdown>
  );
};
